/**
 * Main orchestration for the Pisa Tower simulation
 */

import { PhysicsSimulation } from './simulation/physics';
import { ViewportRenderer } from './rendering/viewport';
import { GraphsRenderer } from './rendering/graphs';
import { Controls } from './ui/controls';

/**
 * Main application class
 */
class PisaTowerApp {
  private simulation: PhysicsSimulation;
  private viewportRenderer: ViewportRenderer;
  private graphsRenderer: GraphsRenderer;
  private controls: Controls;
  private animationFrameId: number | null = null;
  private lastTime: number = 0;

  constructor() {
    // Initialize controls first to get initial parameters
    this.controls = new Controls();
    const initialParams = this.controls.getParams();

    // Initialize simulation
    this.simulation = new PhysicsSimulation(initialParams);

    // Initialize renderers
    const viewportCanvas = document.getElementById('viewport') as HTMLCanvasElement;
    const positionCanvas = document.getElementById('positionGraph') as HTMLCanvasElement;
    const velocityCanvas = document.getElementById('velocityGraph') as HTMLCanvasElement;

    if (!viewportCanvas || !positionCanvas || !velocityCanvas) {
      throw new Error('Could not find canvas elements');
    }

    this.viewportRenderer = new ViewportRenderer(viewportCanvas);
    this.graphsRenderer = new GraphsRenderer(positionCanvas, velocityCanvas);

    // Set up control callbacks
    this.setupControlCallbacks();

    // Set up window resize handler
    this.setupResizeHandler();

    // Initial render
    this.render();

    // Start animation loop
    this.startAnimationLoop();
  }

  private setupControlCallbacks(): void {
    // Handle parameter changes
    this.controls.onParamChange((changedParams) => {
      const currentParams = this.controls.getParams();

      // If only timeScale changed, update params without resetting
      const onlyTimeScaleChanged =
        Object.keys(changedParams).length === 1 &&
        'timeScale' in changedParams;

      if (onlyTimeScaleChanged) {
        this.simulation.updateParams(currentParams);
      } else {
        // For other parameter changes, reset the simulation
        this.simulation.reset(currentParams);
      }

      this.render();
    });

    // Handle play button
    this.controls.onPlay(() => {
      this.simulation.play();
    });

    // Handle pause button
    this.controls.onPause(() => {
      this.simulation.pause();
    });

    // Handle reset button
    this.controls.onReset(() => {
      const currentParams = this.controls.getParams();
      this.simulation.reset(currentParams);
      this.render();
      this.controls.updatePlayButton(false, false);
    });
  }

  private setupResizeHandler(): void {
    const viewportCanvas = document.getElementById('viewport') as HTMLCanvasElement;
    const parent = viewportCanvas.parentElement;

    if (parent) {
      // Use ResizeObserver for better resize detection
      const resizeObserver = new ResizeObserver(() => {
        this.viewportRenderer.resize();
        this.render();
      });

      resizeObserver.observe(parent);
    }
  }

  private startAnimationLoop(): void {
    const animate = (currentTime: number) => {
      // Calculate delta time in seconds
      const dt = this.lastTime ? (currentTime - this.lastTime) / 1000 : 0;
      this.lastTime = currentTime;

      // Clamp dt to avoid huge jumps (e.g., when tab is hidden)
      const clampedDt = Math.min(dt, 0.1);

      // Update simulation
      if (this.simulation.state.running) {
        this.simulation.step(clampedDt);

        // Update play button state if simulation completed
        if (this.simulation.isComplete()) {
          this.controls.updatePlayButton(false, true);
        }
      }

      // Render
      this.render();

      // Continue loop
      this.animationFrameId = requestAnimationFrame(animate);
    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  private render(): void {
    // Render viewport
    this.viewportRenderer.render(this.simulation.state);

    // Render graphs
    this.graphsRenderer.render(
      this.simulation.state.blueHistory,
      this.simulation.state.redHistory
    );
  }

  destroy(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }
}

/**
 * Factory function for embedding
 */
export function create(): PisaTowerApp {
  return new PisaTowerApp();
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PisaTowerApp();
  });
} else {
  new PisaTowerApp();
}

// Export for embedding
export default { create };
